import { createReducer } from "typesafe-actions";

import { EntityType } from "@mapmycustomers/shared/enum";
import { EntitiesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";
import { OutOfCadenceEntity } from "@mapmycustomers/shared/types/entity/activities/OutOfCadenceEntity";

import MmcNotification from "@app/types/MmcNotification";

import {
  Actions,
  clearNotifications,
  clickOnNotification,
  deleteNotification,
  deleteNotifications,
  fetchNotifications,
  fetchNotificationsUnreadTotal,
  hideBouncedEmailModal,
  hideCadenceModal,
  hideNotifications,
  showBouncedEmailModal,
  showCadenceModal,
  showNotifications,
  unsubscribeBouncedEmailEntity,
  updateBouncedEmailEntityEmail,
  updateNotificationReadStatus,
} from "./actions";

export interface NotificationState {
  bouncedEmailModal: {
    entity?: EntitiesSupportedByEmailFeature;
    entityEmailUpdating: boolean;
    notification?: MmcNotification;
    visible: boolean;
  };
  cadenceModal: {
    entities: OutOfCadenceEntity[];
    loading: boolean;
    notification?: MmcNotification;
    visible: boolean;
  };
  deleting: boolean;
  loading: boolean;
  notifications: MmcNotification[];
  onlyMentions: boolean;
  total: number;
  unreadTotal: number;
  unreadUnfilteredTotal: number;
  visible: boolean;
}

const initialState: NotificationState = {
  bouncedEmailModal: {
    entityEmailUpdating: false,
    visible: false,
  },
  cadenceModal: {
    entities: [],
    loading: false,
    notification: undefined,
    visible: false,
  },
  deleting: false,
  loading: false,
  notifications: [],
  onlyMentions: false,
  total: 0,
  unreadTotal: 0,
  unreadUnfilteredTotal: 0,
  visible: false,
};

const notification = createReducer<NotificationState, Actions>(initialState)
  .handleAction(
    fetchNotifications.request,
    (state, { payload: { onlyMentions, reload, silent, updateTotalsOnly } }) =>
      // A silent (background poll) request never touches the visible panel state.
      silent
        ? state
        : {
            ...state,
            loading: !updateTotalsOnly,
            notifications:
              updateTotalsOnly || (!!onlyMentions === state.onlyMentions && !reload)
                ? state.notifications
                : [],
            onlyMentions: onlyMentions ?? state.onlyMentions,
          }
  )
  .handleAction(
    fetchNotifications.success,
    (state, { payload: { notifications, silent, total, unreadTotal } }) => ({
      ...state,
      loading: silent ? state.loading : false,
      notifications: silent ? state.notifications : [...state.notifications, ...notifications],
      total: silent ? state.total : total,
      unreadTotal,
    })
  )
  .handleAction(fetchNotifications.failure, (state) => ({
    ...state,
    loading: false,
  }))
  .handleAction(deleteNotifications.request, (state) => ({
    ...state,
    deleting: true,
  }))
  .handleAction(deleteNotifications.success, (state) => ({
    ...state,
    deleting: false,
    notifications: [],
    total: 0,
    unreadTotal: 0,
    unreadUnfilteredTotal: state.onlyMentions ? state.unreadUnfilteredTotal : 0,
  }))
  .handleAction(deleteNotifications.failure, (state) => ({
    ...state,
    deleting: false,
  }))
  .handleAction(deleteNotification.request, (state, { payload: { id } }) => ({
    ...state,
    notifications: state.notifications.filter((notification) => notification.id !== id),
  }))
  .handleAction(fetchNotificationsUnreadTotal.success, (state, { payload }) => ({
    ...state,
    unreadUnfilteredTotal: payload,
  }))
  .handleAction(clearNotifications, (state) => ({
    ...state,
    notifications: [],
  }))
  .handleAction(showNotifications, (state) => ({
    ...state,
    visible: true,
  }))
  .handleAction(hideNotifications, (state) => ({
    ...state,
    visible: false,
  }))
  .handleAction(
    clickOnNotification,
    (
      state,
      {
        payload: {
          notification: { entity },
        },
      }
    ) => ({
      ...state,
      visible:
        entity && entity.length === 1 && entity[0].type === EntityType.ACTIVITY
          ? state.visible
          : false,
    })
  )
  .handleAction(updateNotificationReadStatus.request, (state, { payload: { id, readStatus } }) => ({
    ...state,
    notifications: state.notifications.map((notification) => ({
      ...notification,
      readStatus: id === notification.id ? readStatus : notification.readStatus,
    })),
  }))
  .handleAction(showCadenceModal.request, (state) => ({
    ...state,
    cadenceModal: {
      entities: [],
      loading: true,
      notification: undefined,
      visible: true,
    },
  }))
  .handleAction(showCadenceModal.success, (state, { payload: { entities, notification } }) => ({
    ...state,
    cadenceModal: {
      entities: entities,
      loading: false,
      notification,
      visible: true,
    },
  }))
  .handleAction([showCadenceModal.failure, hideCadenceModal], (state) => ({
    ...state,
    cadenceModal: {
      entities: [],
      loading: false,
      notification: undefined,
      visible: false,
    },
  }))
  .handleAction(showBouncedEmailModal.success, (state, { payload: { entity, notification } }) => ({
    ...state,
    bouncedEmailModal: {
      ...state.bouncedEmailModal,
      entity,
      notification,
      visible: true,
    },
  }))
  .handleAction(hideBouncedEmailModal, (state) => ({
    ...state,
    bouncedEmailModal: {
      entity: undefined,
      entityEmailUpdating: false,
      notification: undefined,
      visible: false,
    },
  }))
  .handleAction(unsubscribeBouncedEmailEntity.success, (state) => ({
    ...state,
    bouncedEmailModal: {
      ...state.bouncedEmailModal,
      entity: state.bouncedEmailModal.entity
        ? {
            ...state.bouncedEmailModal.entity,
            subscribed: false,
          }
        : undefined,
    },
  }))
  .handleAction(updateBouncedEmailEntityEmail.request, (state) => ({
    ...state,
    bouncedEmailModal: {
      ...state.bouncedEmailModal,
      entityEmailUpdating: true,
    },
  }))
  .handleAction(
    [updateBouncedEmailEntityEmail.success, updateBouncedEmailEntityEmail.failure],
    (state) => ({
      ...state,
      bouncedEmailModal: {
        ...state.bouncedEmailModal,
        entityEmailUpdating: false,
      },
    })
  );

export * from "./selectors";
export type NotificationsActions = Actions;
export default notification;
